import type { PostFormValues } from "@circle-react/components/QuickPostV3/data/types";
import { useComposerSpace } from "@circle-react/components/QuickPostV3/hooks/useComposerSpace";
import {
  isValidBody,
  isValidName,
  isValidTiptapBody,
} from "@circle-react/components/QuickPostV3/validation";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { POST_STATUS_SCHEDULED } from "@circle-react/helpers/postHelpers";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

export const useSubmitEligibility = () => {
  const {
    formState: { isSubmitting },
    watch,
  } = useFormContext<PostFormValues>();
  const { isLoading: isLoadingSpaces } = useSpacesContext();
  const { space, spaceId } = useComposerSpace();
  const currentPostContext = useCurrentPostContext();
  const postHelpers = currentPostContext.helpers;
  const isSubmitEnabled = currentPostContext.isSubmitEnabled ?? true;
  const isEdit = Boolean(currentPostContext.isEdit);
  const [name, body, tiptapBody, topics, status, publishedAt] = watch([
    "name",
    "body",
    "tiptap_body",
    "topics",
    "status",
    "published_at",
  ]);
  const isUsingTiptap = !isEdit || Boolean(postHelpers?.isTiptapPost);
  const hasValidContent = isUsingTiptap
    ? isValidTiptapBody(tiptapBody ?? undefined)
    : isValidBody(body);

  const isMissingScheduledDate =
    status === POST_STATUS_SCHEDULED && !publishedAt;

  const errorMessage = useMemo(() => {
    if (!spaceId) return t("quick_post_v2.error_messages.no_space");
    if (!isValidName(name) && !hasValidContent) {
      return t("quick_post_v2.error_messages.no_title_or_body");
    }
    if (space?.require_topic_selection && !topics?.length) {
      return t("quick_post_v2.error_messages.no_topics");
    }
    return null;
  }, [hasValidContent, name, space, spaceId, topics]);

  return {
    errorMessage,
    isDisabled:
      Boolean(errorMessage) ||
      isMissingScheduledDate ||
      isLoadingSpaces ||
      isSubmitting ||
      !isSubmitEnabled,
  };
};
